import { useRef, useState } from 'react';

import { ArrowDown, ArrowUp, ArrowsUpDown } from '@freelancelabs/icons';
import { SORT_DIRECTION, eventBus } from '@freelancelabs/shared';
import { clsx } from '@freelancelabs/utils';

import { Info } from '../../index';
import { CircleLoader } from '../loader';

export interface TableHeaderCellProps extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'id' | 'label'> {
    id: string;
    label: string;
    minWidth: number;
    textAlign?: 'start' | 'end';
    textHelp?: string;
    position?: number;
    children?: React.ReactNode;
    direction?: SORT_DIRECTION;
    isLoading?: boolean;
    maxWidth?: number;
    onSort?: (arg0: SORT_DIRECTION) => void;
    onSortEventName?: string;
    resizer?: boolean;
    mouseDown?: string;
    setMouseDown?: (arg: string | null) => void;
}

const TableHeaderCell = ({
    id,
    children,
    direction,
    isLoading,
    onSort,
    onSortEventName,
    className,
    minWidth,
    maxWidth,
    textAlign,
    label,
    textHelp,
    resizer,
    mouseDown,
    setMouseDown,
    ...rest
}: TableHeaderCellProps) => {
    const [width, setWidth] = useState(minWidth);
    const previousClientX = useRef(0);
    const style = {
        ...(minWidth && { minWidth: `${minWidth}px` }),
        ...(maxWidth && { maxWidth: `${maxWidth}px` }),
        ...(width && { width: `${width}px` }),
    };
    const align = textAlign === 'end' ? 'end' : '';
    const header = (
        <div className="fl-table-header-cell-title">
            <span className={clsx('label', align)}>{label}</span>
            {textHelp && <Info title={textHelp} />}
        </div>
    );
    /**
     * L'INDICATEUR DE TRI, tel que la charte V3 le dessine.
     *
     * La double flèche fantôme quand la colonne n'est pas triée, la flèche SEULE dans
     * le sens du tri quand elle l'est. Elle occupe sa place dans les deux cas : sans
     * ça, le libellé saute de douze pixels au premier clic, sur toute la rangée.
     *
     * Attention, C'EST UNE ICÔNE ET NON UN CARACTÈRE. La charte l'écrit (« un
     * caractère texte n'est pas une icône ») et la raison est visible : un glyphe suit
     * la fonte, donc ni sa graisse ni son centrage ne s'accordent au reste de la
     * rangée.
     */
    const sortIcon =
        direction === SORT_DIRECTION.ASC ? (
            <ArrowUp size={12} className="fl-table-header-cell-sort-icon selected" />
        ) : direction === SORT_DIRECTION.DSC ? (
            <ArrowDown size={12} className="fl-table-header-cell-sort-icon selected" />
        ) : (
            <ArrowsUpDown size={12} className="fl-table-header-cell-sort-icon" />
        );

    /**
     * Attention, UN VRAI `<button>`, ET CE N'EST PAS UNE PRÉFÉRENCE DE BALISAGE.
     *
     * C'était un `<div role="button" tabIndex={0}> onClick`, donc une cellule que le
     * clavier pouvait atteindre et **pas actionner** : un `onClick` sur un `div` ne se
     * déclenche ni sur Entrée ni sur Espace, là où un bouton fait les deux sans une
     * ligne de code. Le tri du back-office était à la souris seule, sans que rien ne
     * le signale.
     *
     * `type="button"` parce qu'un bouton dans un formulaire l'envoie par défaut ; il
     * n'y en a pas dans une table aujourd'hui, et c'est le genre de défaut qui
     * n'apparaît que le jour où on en pose un.
     */
    const content = onSort ? (
        <button
            type="button"
            className="fl-table-header-cell-sort"
            onClick={() => {
                const newDirection = direction === SORT_DIRECTION.DSC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC;
                onSort(newDirection);
                if (id && onSortEventName) {
                    eventBus.emit(onSortEventName, {
                        id,
                        direction: newDirection,
                    });
                }
            }}
        >
            {header}
            <span className="fl-table-header-cell-sort-container">{sortIcon}</span>
            {isLoading && <CircleLoader />}
        </button>
    ) : (
        header
    );
    const mouseMoveHandler = (e: MouseEvent) => {
        const move = e.clientX - previousClientX.current;
        const newWidth = width + move;
        setWidth(newWidth > minWidth ? newWidth : minWidth);
    };
    const mouseUpHandler = () => {
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
        setMouseDown?.(null);
        previousClientX.current = 0;
    };

    return (
        <th
            style={style}
            className={clsx('fl-table-header-cell', onSort && 'is-sortable', align, className)}
            scope="col"
            /*
             * L'ÉTAT DU TRI SE PORTE ICI, sur la cellule, et il manquait entièrement.
             * Le composant n'avait que deux carets et une classe `selected` : à l'œil
             * la colonne triée se voyait, à la voix rien ne la distinguait des autres.
             * `aria-sort` est l'attribut prévu pour ça, et il va sur le `th`, jamais
             * sur le bouton qu'il contient.
             *
             * `none` plutôt que rien sur une colonne triable non triée : c'est ce qui
             * dit « celle-ci se trie, mais pas maintenant », là que l'absence
             * d'attribut dit « celle-ci ne se trie pas ».
             */
            aria-sort={
                onSort
                    ? direction === SORT_DIRECTION.ASC
                        ? 'ascending'
                        : direction === SORT_DIRECTION.DSC
                          ? 'descending'
                          : 'none'
                    : undefined
            }
            {...rest}
        >
            {content}
            {resizer && (
                <div
                    className={clsx(
                        'fl-table-header-cell-resizer',
                        mouseDown === id && 'fl-table-header-cell-resizer-down'
                    )}
                    onMouseDown={(e) => {
                        setMouseDown?.(id);
                        previousClientX.current = e.clientX;
                        document.addEventListener('mousemove', mouseMoveHandler);
                        document.addEventListener('mouseup', mouseUpHandler);
                    }}
                    onMouseOver={() => setMouseDown?.(id)}
                    onMouseOut={() => {
                        if (!previousClientX.current) {
                            setMouseDown?.(null);
                        }
                    }}
                />
            )}
        </th>
    );
};

export default TableHeaderCell;
